/**
 * Build Logs Page
 * Display Packer build history with stage details
 */

import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as InProgressIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useApiQuery } from '../hooks/useApiQuery';
import { TableSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';

interface BuildStage {
  name: string;
  status: string;
  durationSeconds: number;
  startedAt: string;
  completedAt?: string;
}

interface Build {
  buildId: string;
  workloadId: string;
  tier: number;
  stage: string;
  startedAt: string;
  completedAt?: string;
  totalDuration: number;
  status: string;
  buildVersion?: string;
  stages: BuildStage[];
}

export const BuildLogsPage: React.FC = () => {
  const [workloadFilter, setWorkloadFilter] = useState<string>('all');
  const [tierFilter, setTierFilter] = useState<string>('all');

  const { data: builds, loading, error, refetch } = useApiQuery<Build[]>(
    async () => {
      const params = new URLSearchParams();
      if (workloadFilter !== 'all') {
        params.append('workloadId', workloadFilter);
      }
      if (tierFilter !== 'all') {
        params.append('tier', tierFilter);
      }
      params.append('limit', '50');

      const response = await fetch(`/api/builds?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch build logs');
      }
      const data = await response.json();
      return data.data || [];
    },
    [workloadFilter, tierFilter]
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      default:
        return <InProgressIcon color="info" fontSize="small" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'started':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getTierLabel = (tier: number): string => {
    switch (tier) {
      case 1:
        return 'Tier 1: Base Windows';
      case 2:
        return 'Tier 2: Workload Base';
      case 3:
        return 'Tier 3: Final Image';
      default:
        return `Tier ${tier}`;
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Build Logs
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Packer build history with stage-level details and performance metrics
          </Typography>
        </Box>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={refetch} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <ErrorMessage
          message={error}
          title="Build-Logs konnten nicht geladen werden"
          onRetry={refetch}
        />
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Workload</InputLabel>
              <Select
                value={workloadFilter}
                label="Workload"
                onChange={(e) => setWorkloadFilter(e.target.value)}
              >
                <MenuItem value="all">All Workloads</MenuItem>
                <MenuItem value="it-dev">IT-Dev</MenuItem>
                <MenuItem value="cam">CAM (HyperMill)</MenuItem>
                <MenuItem value="bim">BIM (Inventor)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier</InputLabel>
              <Select
                value={tierFilter}
                label="Tier"
                onChange={(e) => setTierFilter(e.target.value)}
              >
                <MenuItem value="all">All Tiers</MenuItem>
                <MenuItem value="1">Tier 1 (Base Windows)</MenuItem>
                <MenuItem value="2">Tier 2 (Workload Base)</MenuItem>
                <MenuItem value="3">Tier 3 (Final)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Build Logs Table */}
      {loading && !builds ? (
        <TableSkeleton rows={5} columns={7} />
      ) : (builds || []).length === 0 ? (
        <Alert severity="info">
          No build logs found. Builds will appear here once the multi-stage Packer pipeline runs.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="5%"></TableCell>
                <TableCell>Build ID</TableCell>
                <TableCell>Workload</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stages</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(builds || []).map((build) => (
                <TableRow key={build.buildId}>
                  <TableCell colSpan={8} sx={{ p: 0 }}>
                    <Accordion disableGutters elevation={0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: 2,
                            px: 2,
                          }}
                        >
                          <Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>
                            {getStatusIcon(build.status)}
                          </Box>
                          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              {build.buildId}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: '0 0 100px' }}>
                            <Chip label={build.workloadId} size="small" variant="outlined" />
                          </Box>
                          <Box sx={{ flex: '0 0 150px' }}>
                            <Typography variant="caption" color="text.secondary">
                              {getTierLabel(build.tier)}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: '0 0 120px' }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDistanceToNow(new Date(build.startedAt), { addSuffix: true })}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: '0 0 80px' }}>
                            <Typography variant="body2">{formatDuration(build.totalDuration)}</Typography>
                          </Box>
                          <Box sx={{ flex: '0 0 100px' }}>
                            <Chip
                              label={build.status}
                              size="small"
                              color={getStatusColor(build.status)}
                            />
                          </Box>
                          <Box sx={{ flex: '0 0 80px' }}>
                            <Typography variant="body2">{build.stages.length}</Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Build Stages
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Stage Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Started</TableCell>
                                <TableCell>Completed</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {build.stages.map((stage, index) => (
                                <TableRow key={index}>
                                  <TableCell>{stage.name}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={stage.status}
                                      size="small"
                                      color={getStatusColor(stage.status)}
                                      icon={getStatusIcon(stage.status)}
                                    />
                                  </TableCell>
                                  <TableCell>{formatDuration(stage.durationSeconds)}</TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {new Date(stage.startedAt).toLocaleString()}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="caption">
                                      {stage.completedAt
                                        ? new Date(stage.completedAt).toLocaleString()
                                        : '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {build.buildVersion && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Build Version: {build.buildVersion}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};
